<script>
  import { link, querystring } from 'svelte-spa-router';
  import { authApi } from '../stores/api.js';
  import { authStore, logout } from '../stores/auth.js';
  import { _ } from '../lib/i18n.js';
  import AuthCard from '../components/AuthCard.svelte';
  import AuthNotice from '../components/AuthNotice.svelte';

  // The mailed link is `#/reset-password?token=…`: the token is in the hash.
  $: token = new URLSearchParams($querystring || '').get('token') || '';

  let password = '';
  let confirmPassword = '';
  let isLoading = false;
  /** `done` | `revoking` once the password is changed. */
  let outcome = '';
  let error = '';
  /** Whether the error can only be fixed with a new link. */
  let needsNewLink = false;

  async function handleSubmit(event) {
    event.preventDefault();
    if (isLoading) return;
    error = '';
    needsNewLink = false;
    if (password !== confirmPassword) {
      error = $_('auth.reset.mismatch');
      return;
    }

    isLoading = true;
    try {
      const result = await authApi.resetPassword(token, password);
      if (result.httpStatus === 200 && result.status === 'OK') {
        // Without the header the state is unknown: say the cautious thing.
        outcome = result.revocationState === 'done' ? 'done' : 'revoking';
        password = '';
        confirmPassword = '';
        // Every session from before the reset is ended on the server; drop
        // this browser's copy too, so the next step is a fresh sign-in.
        if ($authStore.isAuthenticated) await logout();
      } else if (result.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
        error = $_('auth.reset.invalidToken');
        needsNewLink = true;
      } else if (result.status === 'FIELD_ERROR') {
        error = $_('auth.reset.weakPassword');
      } else if (result.httpStatus === 409) {
        error = $_('auth.reset.inProgress');
        needsNewLink = true;
      } else {
        error = $_('auth.reset.error');
      }
    } catch {
      error = $_('auth.reset.error');
    } finally {
      isLoading = false;
    }
  }
</script>

<AuthCard title={$_('auth.reset.title')}>
  {#if outcome === 'done'}
    <AuthNotice tone="success" testid="reset-password-done">{$_('auth.reset.done')}</AuthNotice>
    <a href="/login" use:link class="w-full btn btn-primary h-12 text-base">{$_('auth.reset.goToLogin')}</a>
  {:else if outcome === 'revoking'}
    <AuthNotice tone="info" testid="reset-password-revoking">{$_('auth.reset.revoking')}</AuthNotice>
    <a href="/login" use:link class="w-full btn btn-primary h-12 text-base">{$_('auth.reset.goToLogin')}</a>
  {:else if !token}
    <AuthNotice tone="error" testid="reset-password-missing-token">{$_('auth.reset.missingToken')}</AuthNotice>
    <a href="/forgot-password" use:link class="w-full btn btn-primary h-12 text-base">{$_('auth.reset.requestNew')}</a>
  {:else}
    {#if error}
      <AuthNotice tone="error" testid="reset-password-error">{error}</AuthNotice>
    {/if}
    {#if needsNewLink}
      <a href="/forgot-password" use:link class="w-full btn btn-primary h-12 text-base">{$_('auth.reset.requestNew')}</a>
    {:else}
      <form on:submit={handleSubmit} class="space-y-5">
        <div>
          <label for="reset-password" class="label">{$_('auth.reset.newPassword')}</label>
          <input
            id="reset-password"
            type="password"
            bind:value={password}
            required
            minlength="8"
            autocomplete="new-password"
            class="input"
            disabled={isLoading}
          />
          <p class="mt-2 text-xs" style="color: var(--color-text-muted);">{$_('auth.reset.passwordHint')}</p>
        </div>
        <div>
          <label for="reset-password-confirm" class="label">{$_('auth.reset.confirmPassword')}</label>
          <input
            id="reset-password-confirm"
            type="password"
            bind:value={confirmPassword}
            required
            autocomplete="new-password"
            class="input"
            disabled={isLoading}
          />
        </div>
        <button type="submit" class="w-full btn btn-primary h-12 text-base" disabled={isLoading || !password || !confirmPassword}>
          {isLoading ? `${$_('common.loading')}...` : $_('auth.reset.submit')}
        </button>
      </form>
    {/if}
  {/if}
  <p class="text-center text-sm">
    <a href="/login" use:link class="font-bold text-primary-500 hover:underline">{$_('auth.backToLogin')}</a>
  </p>
</AuthCard>
